import { Search, Building2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";

import { CondominiumCard } from "@/components/Condominium/CondominiumCard";
import { CreateCondominiumDialog } from "@/components/Condominium/CondominiumCreateDialog";
import { Sidebar } from "@/components/Sidebar";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

interface Condominium {
	id: number;
	attributes: {
		Name: string;
		region: string;
		street: string;
		neighborhood: string;
		administratorName: string;
		number: string;
		commercialStatus: string;
		managerName: string;
		managerPhone: string;
		caretakerName: string;
		caretakerPhone: string;
		constructionYear: string;
		zipCode?: string;
		builderName?: string;
		developerName?: string;
		numberOfServiceElevators?: string;
		numberOfPassengerElevators?: string;
		deliverySchedule?: string;
		corner?: boolean;
		guardhouse?: boolean;
		intercom?: boolean;
		terrace?: boolean;
		undergroundGarage?: boolean;
		beachService?: boolean;
		beachEquipmentStorage?: boolean;
		electronicGate?: boolean;
		groundFloorGarage?: boolean;
		collectiveGas?: boolean;
	};
}

interface ApiResponse {
	data: Condominium[];
	meta: {
		pagination: {
			page: number;
			pageSize: number;
			pageCount: number;
			total: number;
		};
	};
}

export function CondominiumControl() {
	const [condominiums, setCondominiums] = useState<Condominium[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterType, setFilterType] = useState<"name" | "region">("name");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [loading, setLoading] = useState(false);

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const isAdmin = true;

	const itemsPerPage = 10;
	const route = import.meta.env.VITE_URL_ENDPOINT;

	const buildQuery = useCallback(() => {
		const params = new URLSearchParams();
		params.append("pagination[page]", String(currentPage));
		params.append("pagination[pageSize]", String(itemsPerPage));
		if (searchTerm.trim()) {
			const field = filterType === "name" ? "Name" : "region";
			params.append(`filters[${field}][$containsi]`, searchTerm.trim());
		}
		return params.toString();
	}, [currentPage, itemsPerPage, searchTerm, filterType]);

	const fetchCondominiums = useCallback(async () => {
		setLoading(true);
		try {
			const qs = buildQuery();
			const res = await fetch(`${route}/api/condominiums?${qs}&sort=Name:asc`);
			if (!res.ok) throw new Error("Erro na resposta da API");
			const json: ApiResponse = await res.json();
			setCondominiums(json.data);
			setTotalPages(json.meta.pagination.pageCount);
		} catch (err) {
			console.error("Não foi possível buscar condomínios:", err);
			setCondominiums([]);
		} finally {
			setLoading(false);
		}
	}, [buildQuery, route]);

	useEffect(() => {
		fetchCondominiums();
	}, [fetchCondominiums]);

	const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		setCurrentPage(1);
	};

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="flex min-h-screen">
				<Sidebar
					sidebarOpen={sidebarOpen}
					setSidebarOpen={setSidebarOpen}
					isAdmin={isAdmin}
				/>

				{/* Overlay for mobile */}
				{sidebarOpen && (
					<div
						className="fixed inset-0 z-40 bg-black/50 lg:hidden"
						onClick={() => setSidebarOpen(false)}
					/>
				)}

				{/* Main Content */}
				<div className="flex flex-1 flex-col lg:ml-0">
					{/* Top Header */}
					<header className="border-b border-gray-200 bg-white shadow-sm">
						<div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
							<div className="flex items-center">
								<button
									onClick={() => setSidebarOpen(!sidebarOpen)}
									className="rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 lg:hidden"
								>
									<i className="fas fa-bars text-xl"></i>
								</button>
								<h1 className="ml-4 text-xl font-semibold text-gray-900 sm:text-2xl lg:ml-0">
									Gerenciamento de Edifício
								</h1>
							</div>
						</div>
					</header>

					{/* Filtros e conteúdo */}
					<div className="flex-1 p-8">
						<div className="mx-auto max-w-6xl">
							<h1 className="mb-3 text-center text-2xl font-bold">
								Lista de Condomínios
							</h1>

							{/* Filtros */}
							<div className="mb-6 flex flex-col space-y-4">
								<div className="flex space-x-4">
									<div className="relative flex-grow">
										<span className="absolute inset-y-0 left-0 flex items-center pl-3">
											<Search className="text-gray-400" />
										</span>
										<Input
											value={searchTerm}
											onChange={onSearchChange}
											className="w-full pl-10"
											placeholder={`Buscar por ${
												filterType === "name" ? "nome" : "região"
											} do condomínio`}
										/>
									</div>
									<Select
										value={filterType}
										onValueChange={(v: "name" | "region") => {
											setFilterType(v);
											setCurrentPage(1);
										}}
									>
										<SelectTrigger className="w-[180px]">
											<SelectValue placeholder="Filtrar por" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="name">Nome</SelectItem>
											<SelectItem value="region">Região</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<CreateCondominiumDialog
									onCondominiumCreate={fetchCondominiums}
									trigger={
										<button className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-2.5 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto">
											<Building2 size={20} />
											<span className="font-medium">Cadastrar Condomínio</span>
										</button>
									}
								/>
							</div>

							{/* Lista de condomínios */}
							{loading ? (
								<div className="flex items-center justify-center py-16">
									<div className="text-center">
										<div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
										<p className="text-gray-600">Carregando condomínios...</p>
									</div>
								</div>
							) : (
								<div className="mt-4 space-y-4">
									{condominiums.length > 0 ? (
										condominiums.map((condominium) => (
											<CondominiumCard
												key={condominium.id}
												id={condominium.id}
												name={condominium.attributes.Name}
												region={condominium.attributes.region}
												street={condominium.attributes.street}
												neighborhood={condominium.attributes.neighborhood}
												number={condominium.attributes.number}
												administratorName={
													condominium.attributes.administratorName
												}
												commercialStatus={
													condominium.attributes.commercialStatus
												}
												managerName={condominium.attributes.managerName}
												managerPhone={condominium.attributes.managerPhone}
												caretakerName={condominium.attributes.caretakerName}
												caretakerPhone={condominium.attributes.caretakerPhone}
												constructionYear={
													condominium.attributes.constructionYear
												}
												zipCode={condominium.attributes.zipCode}
												builderName={condominium.attributes.builderName}
												developerName={condominium.attributes.developerName}
												numberOfServiceElevators={
													condominium.attributes.numberOfServiceElevators
												}
												numberOfPassengerElevators={
													condominium.attributes.numberOfPassengerElevators
												}
												deliverySchedule={
													condominium.attributes.deliverySchedule
												}
												corner={condominium.attributes.corner}
												guardhouse={condominium.attributes.guardhouse}
												intercom={condominium.attributes.intercom}
												terrace={condominium.attributes.terrace}
												undergroundGarage={
													condominium.attributes.undergroundGarage
												}
												beachService={condominium.attributes.beachService}
												beachEquipmentStorage={
													condominium.attributes.beachEquipmentStorage
												}
												electronicGate={condominium.attributes.electronicGate}
												groundFloorGarage={
													condominium.attributes.groundFloorGarage
												}
												collectiveGas={condominium.attributes.collectiveGas}
												onCondominiumUpdate={fetchCondominiums}
											/>
										))
									) : (
										<div className="flex flex-col items-center justify-center py-16 text-center">
											<div className="mb-4 rounded-full bg-gray-100 p-6">
												<Building2
													className="text-4xl text-gray-400"
													size={48}
												/>
											</div>
											<h3 className="mb-2 text-xl font-semibold text-gray-900">
												Nenhum condomínio encontrado
											</h3>
											<p className="text-gray-600">
												Tente ajustar os filtros ou cadastre um novo condomínio.
											</p>
										</div>
									)}
								</div>
							)}

							{/* Paginação estilizada */}
							{totalPages > 1 && (
								<div className="mt-8 flex flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-x-2 sm:space-y-0">
									<button
										onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
										disabled={currentPage === 1}
										className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<i className="fas fa-chevron-left"></i>
										<span>Anterior</span>
									</button>

									<div className="flex space-x-1">
										{(() => {
											const pages = [];
											const maxVisiblePages = 5;
											const halfVisible = Math.floor(maxVisiblePages / 2);

											let startPage = Math.max(1, currentPage - halfVisible);
											let endPage = Math.min(
												totalPages,
												startPage + maxVisiblePages - 1,
											);

											if (endPage - startPage + 1 < maxVisiblePages) {
												startPage = Math.max(1, endPage - maxVisiblePages + 1);
											}

											if (startPage > 1) {
												pages.push(
													<button
														key={1}
														onClick={() => setCurrentPage(1)}
														className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
													>
														1
													</button>,
												);
												if (startPage > 2) {
													pages.push(
														<span
															key="dots1"
															className="flex h-10 w-10 items-center justify-center text-gray-500"
														>
															...
														</span>,
													);
												}
											}

											for (let i = startPage; i <= endPage; i++) {
												pages.push(
													<button
														key={i}
														onClick={() => setCurrentPage(i)}
														className={`h-10 w-10 rounded-lg text-sm font-medium ${
															currentPage === i
																? "bg-blue-600 text-white"
																: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
														}`}
													>
														{i}
													</button>,
												);
											}

											if (endPage < totalPages) {
												if (endPage < totalPages - 1) {
													pages.push(
														<span
															key="dots2"
															className="flex h-10 w-10 items-center justify-center text-gray-500"
														>
															...
														</span>,
													);
												}
												pages.push(
													<button
														key={totalPages}
														onClick={() => setCurrentPage(totalPages)}
														className="h-10 w-10 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
													>
														{totalPages}
													</button>,
												);
											}

											return pages;
										})()}
									</div>

									<button
										onClick={() =>
											setCurrentPage((p) => Math.min(p + 1, totalPages))
										}
										disabled={currentPage === totalPages}
										className="flex w-full items-center justify-center space-x-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
									>
										<span>Próxima</span>
										<i className="fas fa-chevron-right"></i>
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
